import styled from '@emotion/styled';
import * as React from 'react';
import { PillFrame } from './PillPrimitives';

export enum DocsCalloutTone {
  INFO = 'info',
  WARNING = 'warning',
  ENTERPRISE = 'enterprise'
}

export interface DocsCalloutWidgetProps {
  children?: React.ReactNode;
  label?: string;
  title?: string;
  tone?: DocsCalloutTone;
}

export const DocsCalloutWidget: React.FC<DocsCalloutWidgetProps> = ({
  children,
  label,
  title,
  tone = DocsCalloutTone.INFO
}) => {
  return (
    <S.Root tone={tone} data-content-callout>
      {label || title ? (
        <S.Header>
          {label ? <S.Pill tone={tone}>{label}</S.Pill> : null}
          {title ? (
            <S.Pill tone={tone} secondary>
              {title}
            </S.Pill>
          ) : null}
        </S.Header>
      ) : null}
      <S.Body>{children}</S.Body>
      <S.QuoteMark tone={tone} aria-hidden="true">
        "
      </S.QuoteMark>
    </S.Root>
  );
};

export const Info: React.FC<Omit<DocsCalloutWidgetProps, 'tone' | 'label'>> = (props) => {
  return <DocsCalloutWidget {...props} tone={DocsCalloutTone.INFO} />;
};

export const Warning: React.FC<Omit<DocsCalloutWidgetProps, 'tone' | 'label'>> = (props) => {
  return <DocsCalloutWidget {...props} tone={DocsCalloutTone.WARNING} />;
};

export const Enterprise: React.FC<Omit<DocsCalloutWidgetProps, 'tone' | 'label'>> = (props) => {
  return <DocsCalloutWidget {...props} tone={DocsCalloutTone.ENTERPRISE} label="Enterprise" />;
};

export const Note: React.FC<Omit<DocsCalloutWidgetProps, 'tone' | 'label'>> = (props) => {
  return <DocsCalloutWidget {...props} tone={DocsCalloutTone.INFO} label="Note" />;
};

export const Tip: React.FC<Omit<DocsCalloutWidgetProps, 'tone' | 'label'>> = (props) => {
  return <DocsCalloutWidget {...props} tone={DocsCalloutTone.INFO} label="Tip" />;
};

namespace S {
  const toneTheme = (theme: any, tone: DocsCalloutTone) =>
    tone === DocsCalloutTone.ENTERPRISE
      ? theme.docsCallout.enterprise
      : tone === DocsCalloutTone.WARNING
        ? theme.docsCallout.warning
        : theme.docsCallout.info;

  export const Root = styled.aside<{ tone: DocsCalloutTone }>`
    padding: 22px 28px 22px 26px;
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    border: 1px solid ${(p) => p.theme.text.listBorder};
    border-left: 4px solid ${(p) => toneTheme(p.theme, p.tone).borderAccent};
    background: ${(p) => toneTheme(p.theme, p.tone).background};
    box-shadow: ${(p) => p.theme.text.blockquoteShadow};
  `;

  export const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
    padding-right: 48px;
  `;

  export const Pill = styled(PillFrame)<{ secondary?: boolean; tone: DocsCalloutTone }>`
    min-height: 28px;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1;
    border: 1px solid ${(p) => toneTheme(p.theme, p.tone).pillBorder};
    background: ${(p) => toneTheme(p.theme, p.tone).pillBackground};
    color: ${(p) => (p.secondary ? p.theme.text.heading : toneTheme(p.theme, p.tone).pillText)};
  `;

  export const Body = styled.div`
    > * + * {
      margin-top: 16px;
    }

    p {
      color: ${(p) => p.theme.text.heading};
      font-size: 15px;
      line-height: 1.75;
      text-align: left;
    }

    strong,
    b {
      color: ${(p) => p.theme.text.heading};
      font-weight: 500;
    }
  `;

  export const QuoteMark = styled.div<{ tone: DocsCalloutTone }>`
    position: absolute;
    top: 12px;
    right: 16px;
    font-family: 'Inter Tight Variable', sans-serif;
    font-size: 72px;
    line-height: 1;
    color: ${(p) => toneTheme(p.theme, p.tone).quoteColor};
    pointer-events: none;
  `;
}
